/**
 * Spanish for Capture (Console V7). The first block is the V7 design's own aithora-es.js
 * entries for the strings this page shows (copied as they are); the second is ours, for the
 * lines our page words from the API (empty states, honest gaps, counts). The rules cover
 * the lines built from a number or a name. Names, codes and server-written text are never
 * translated here. Kept apart from es-extra.ts so a fresh port of the design never loses it.
 */
import type { EsEngine } from './engine';

/** From the V7 design's aithora-es.js (Capture strings). */
const FROM_DESIGN: Record<string, string> = {
  "12 mo": "12 meses",
  "12 months": "12 meses",
  "2027 proposed rates": "Tarifas propuestas para 2027",
  "25th–50th": "pct. 25–50",
  "50th–75th": "pct. 50–75",
  "90-day alert line": "línea de alerta de 90 días",
  "APCM + behavioral health add-on · 2026": "APCM + complemento de salud conductual · 2026",
  "APCM advanced tier": "APCM nivel avanzado",
  "AUDIT FLAG": "ALERTA DE AUDITORÍA",
  "Add agreement": "Agregar acuerdo",
  "Adds per month": "Suma al mes",
  "Agreement": "Acuerdo",
  "Ask Atlas why": "Preguntar a Atlas por qué",
  "Ask provider": "Preguntar al profesional",
  "Assisted living · non-facility rate": "Vivienda asistida · tarifa fuera de centro",
  "At clearinghouse": "En la cámara de compensación",
  "At stake": "En juego",
  "Atlas draft · a person reviews and sends": "Borrador de Atlas · una persona revisa y envía",
  "Atlas drafts. A person reviews and sends. Nothing is sent automatically.": "Atlas redacta. Una persona revisa y envía. Nada se envía automáticamente.",
  "Atlas drafts. A person reviews and sends. Patterns by payer feed the Contracts brief.": "Atlas redacta. Una persona revisa y envía. Los patrones por pagador alimentan el informe de Contratos.",
  "Audit Pack": "Paquete de auditoría",
  "Audit risk": "Riesgo de auditoría",
  "Below median / yr": "Bajo la mediana / año",
  "Best published rate for this code, your region": "Mejor tarifa publicada para este código, su región",
  "Billed for almost every eligible resident. Expect a medical-necessity review": "Facturado para casi todos los residentes elegibles. Espere una revisión de necesidad médica",
  "Billed twice in one month": "Facturado dos veces en un mes",
  "Billed, next 12 months": "Facturado, próximos 12 meses",
  "Blocked residents": "Residentes bloqueados",
  "Blocked residents · all codes": "Residentes bloqueados · todos los códigos",
  "Building's share": "Parte del edificio",
  "By payer": "Por pagador",
  "By practitioner": "Por profesional",
  "By wing": "Por ala",
  "CCM · first 20 min": "CCM · primeros 20 min",
  "CLAIM CHECKLIST": "LISTA DE LA RECLAMACIÓN",
  "Care delivered": "Cuidado prestado",
  "Chance": "Probabilidad",
  "Chance of payment": "Probabilidad de pago",
  "Chronic care mgmt": "Gestión de cuidados crónicos",
  "Claims ready to defend": "Reclamaciones listas para defender",
  "Claims this month": "Reclamaciones este mes",
  "Clock": "Reloj",
  "Codes to start": "Códigos por iniciar",
  "Columns": "Columnas",
  "Commercial": "Comercial",
  "Compared with": "Comparado con",
  "Complex CCM, first 60 min": "CCM complejo, primeros 60 min",
  "Consent → Paid": "Consentimiento → Pagado",
  "Contracts": "Contratos",
  "Current path": "Rumbo actual",
  "Days": "Días",
  "Days to pay": "Días para cobrar",
  "Deadline": "Plazo",
  "Delivered": "Prestado",
  "Denials & Underpayments": "Denegaciones y pagos bajos",
  "Denied, open": "Denegadas, abiertas",
  "Diagnosis missing": "Falta el diagnóstico",
  "Draft appeal": "Redactar apelación",
  "Easiest money first · same-month conflicts checked before anything is suggested": "Primero el dinero más fácil · los conflictos del mismo mes se revisan antes de sugerir nada",
  "Edit draft": "Editar borrador",
  "Enrolled / eligible": "Inscritos / elegibles",
  "Evidence attached": "Evidencia adjunta",
  "Evidence per claim": "Evidencia por reclamación",
  "Export audit bundle (PDF)": "Exportar paquete de auditoría (PDF)",
  "Export forecast": "Exportar pronóstico",
  "Facility Admins see their own building. Comparisons use published Medicare figures; where none exists, the row says so.": "Los administradores del centro ven solo su edificio. Las comparaciones usan cifras publicadas de Medicare; si no existen, la fila lo indica.",
  "Find practitioner": "Buscar profesional",
  "Fit": "Ajuste",
  "Fixed published rate": "Tarifa publicada fija",
  "Gap to median": "Diferencia con la mediana",
  "Green done · outlined is the current step · red is blocked": "Verde: hecho · con contorno: paso actual · rojo: bloqueado",
  "How many succeed": "Cuántos tienen éxito",
  "In forecast": "En el pronóstico",
  "Last export": "Última exportación",
  "Likely range": "Rango probable",
  "Log minutes": "Registrar minutos",
  "Looks rounded to the minimum": "Parece redondeado al mínimo",
  "Low": "Baja",
  "Medicare reference": "Referencia de Medicare",
  "Medicare, same code": "Medicare, mismo código",
  "Minutes": "Minutos",
  "Missing information": "Falta información",
  "Month": "Mes",
  "Month closes in": "El mes cierra en",
  "NPI and date": "NPI y fecha",
  "Nearest deadline": "Plazo más próximo",
  "Needs agreement": "Falta acuerdo",
  "Needs consent": "Falta consentimiento",
  "Needs practitioner": "Falta profesional",
  "Needs practitioner agreement": "Falta acuerdo con el profesional",
  "Next 12 months": "Próximos 12 meses",
  "No agreement": "Sin acuerdo",
  "No agreement on file": "Sin acuerdo en archivo",
  "No appeal · fix and close": "Sin apelación · corregir y cerrar",
  "No issues": "Sin problemas",
  "No public benchmark": "Sin referencia pública",
  "No published rate": "Sin tarifa publicada",
  "None on file": "Nada en archivo",
  "Not billed": "Sin facturar",
  "Not medically necessary per payer": "No médicamente necesario según el pagador",
  "Not published for Medicare Advantage": "No publicado para Medicare Advantage",
  "Nursing facility · facility rate": "Centro de enfermería · tarifa de centro",
  "Open Contracts": "Abrir Contratos",
  "Open Denials": "Abrir Denegaciones",
  "Open Readiness": "Abrir Preparación",
  "Open agreement": "Abrir acuerdo",
  "Open in Contracts": "Abrir en Contratos",
  "Open in Readiness": "Abrir en Preparación",
  "Open items with this payer": "Pendientes con este pagador",
  "Open time logs": "Abrir registros de tiempo",
  "PUBLISHED rates · PFS 2026": "Tarifas PUBLISHED · PFS 2026",
  "Panel load": "Carga del panel",
  "Payer · members": "Pagador · afiliados",
  "Payer · resident": "Pagador · residente",
  "Pays / res / mo": "Paga / res. / mes",
  "Per month": "Por mes",
  "Per year": "Por año",
  "Pick a code to compare rates.": "Elija un código para comparar tarifas.",
  "Practitioner billed": "Facturado por el profesional",
  "Practitioner bills": "Factura el profesional",
  "Practitioner sign-off": "Firma del profesional",
  "Practitioners live here: panel load, agreements, and gaps like Wing C. Residents in a Medicare Part A skilled stay are held automatically: CCM can’t be billed during the stay, but time outside it still counts toward the month (CMS CCM FAQ).": "Aquí están los profesionales: carga de pacientes, acuerdos y vacíos como el Ala C. Los residentes en una estancia especializada de la Parte A de Medicare se retienen automáticamente: el CCM no se puede facturar durante la estancia, pero el tiempo fuera de ella sí cuenta para el mes (preguntas frecuentes de CMS sobre CCM).",
  "QUICK LOOK": "VISTAZO",
  "Qualify": "Califican",
  "Quarter": "Trimestre",
  "RPM device data, 16–30 days": "Datos de dispositivo RPM, 16–30 días",
  "RPM device data, 2–15 days · new in 2026": "Datos de dispositivo RPM, 2–15 días · nuevo en 2026",
  "RPM mgmt": "Gestión RPM",
  "RPM mgmt, first 10 min · new in 2026": "Gestión RPM, primeros 10 min · nuevo en 2026",
  "Range:": "Rango:",
  "Ranked by chance of getting paid × dollars · underpaid = remittance below your contract rate": "Ordenado por probabilidad de cobro × dólares · pago bajo = remesa por debajo de su tarifa contractual",
  "Rates loaded": "Tarifas cargadas",
  "Ready now": "Listo ya",
  "Ready to send": "Listas para enviar",
  "Recovered this year": "Recuperado este año",
  "Remind": "Recordar",
  "Remove from forecast": "Quitar del pronóstico",
  "Renewals, next 180 days": "Renovaciones, próximos 180 días",
  "Residents shown as a summary. Full words from Atlas conversations stay in the record, for staff with access.": "Residentes mostrados en resumen. Las palabras completas de las conversaciones con Atlas quedan en el expediente, para el personal con acceso.",
  "Residents who qualify": "Residentes que califican",
  "Residents who qualify · summary from the care record": "Residentes que califican · resumen del expediente",
  "Review and send appeal": "Revisar y enviar apelación",
  "Rows sorted by money left on the table": "Filas ordenadas por dinero sin cobrar",
  "SCENARIO": "ESCENARIO",
  "Same-month conflicts": "Conflictos del mismo mes",
  "Scenarios on": "Escenarios activos",
  "Send consent forms": "Enviar formularios de consentimiento",
  "Setting": "Entorno",
  "Signed consent": "Consentimiento firmado",
  "Snapshot": "Resumen",
  "Start billing": "Empezar a facturar",
  "Start plan": "Iniciar plan",
  "Starts": "Empieza",
  "The enrolled practitioner bills · Building's share per your signed agreement · Documentation support by Aithora": "Factura el profesional inscrito · Parte del edificio según su acuerdo firmado · Apoyo de documentación de Aithora",
  "Time log": "Registro de tiempo",
  "Timely filing limit passed": "Venció el plazo de presentación",
  "Underpaid": "Pago bajo",
  "Upload contract": "Subir contrato",
  "Upload contract or remittance": "Subir contrato o remesa",
  "Upload remittance": "Subir remesa",
  "WHY THIS CODE": "POR QUÉ ESTE CÓDIGO",
  "What an auditor would ask": "Lo que preguntaría un auditor",
  "What an auditor would question, against published Medicare figures where one exists": "Lo que un auditor cuestionaría, frente a cifras publicadas de Medicare cuando existen",
  "What can I appeal this week?": "¿Qué puedo apelar esta semana?",
  "What is in the way": "Qué lo impide",
  "What it needs": "Qué necesita",
  "What we can already see": "Lo que ya podemos ver",
  "Which renewal matters most?": "¿Qué renovación importa más?",
  "Why, in plain words": "Por qué, en palabras simples",
  "Wing": "Ala",
  "With scenarios": "Con escenarios",
  "Your pattern": "Su patrón",
  "Your rate": "Su tarifa",
  "Your rate:": "Su tarifa:",
  "above 75th": "sobre el pct. 75",
  "all codes, all wings": "todos los códigos, todas las alas",
  "below 25th pct": "bajo el pct. 25",
  "chance × dollars": "probabilidad × dólares",
  "codes you qualify for, not billed": "códigos para los que califica, sin facturar",
  "current and signed": "vigente y firmado",
  "delivered": "prestado",
  "facility setting": "entorno de centro",
  "fix before anyone asks": "corríjalo antes de que alguien pregunte",
  "fixed": "fija",
  "from care records and Oversight": "de los expedientes y Supervisión",
  "in Readiness": "en Preparación",
  "last 12 months, your claims": "últimos 12 meses, sus reclamaciones",
  "no published rate": "sin tarifa publicada",
  "not shown": "no se muestra",
  "or say “Hey Siri, ask Aithora what’s blocking money”": "o diga «Oye Siri, pregúntale a Aithora qué está frenando el dinero»",
  "signed, dated, on file": "firmado, fechado, en archivo",
  "who, when, minutes": "quién, cuándo, minutos",
};

/** Ours: lines the V7 design does not have, worded for this page. */
const OURS: Record<string, string> = {
  'ATLAS · FROM YOUR CLAIMS': 'ATLAS · DE SUS RECLAMACIONES',
  APPEAL: 'APELACIÓN',
  'NEGOTIATION BRIEF': 'INFORME DE NEGOCIACIÓN',
  'Atlas draft': 'Borrador de Atlas',
  Cautious: 'Prudente',
  Expected: 'Esperado',
  Strong: 'Fuerte',
  'Claims after sending are not on this list yet: it shows what each claim still needs.':
    'Las reclamaciones ya enviadas aún no están en esta lista: muestra lo que le falta a cada reclamación.',
  'Commercial, published range': 'Comercial, rango publicado',
  'Consent missing': 'Falta consentimiento',
  'Contract and remittance upload is not built yet.': 'La carga de contratos y remesas aún no está disponible.',
  'CMS sets this rate. Nothing to negotiate. It is the reference for every other payer.':
    'CMS fija esta tarifa. No hay nada que negociar. Es la referencia para todos los demás pagadores.',
  'Forecast is modeled from your last 12 months': 'El pronóstico se modela con sus últimos 12 meses',
  'Medicare Advantage': 'Medicare Advantage',
  'Medicare FFS': 'Medicare FFS',
  FFS: 'FFS',
  MA: 'MA',
  'No billing practitioner linked': 'Sin profesional de facturación vinculado',
  'no billing practitioner linked': 'sin profesional de facturación vinculado',
  'No contract or remittance on file': 'Sin contrato ni remesa en archivo',
  'No contracts on file. Renewal dates come from uploaded contracts.': 'No hay contratos en archivo. Las fechas de renovación salen de los contratos subidos.',
  'No contracts on file for these buildings. Renewal dates come from uploaded contracts.':
    'No hay contratos en archivo para estos edificios. Las fechas de renovación salen de los contratos subidos.',
  'No renewal dates on file.': 'No hay fechas de renovación en archivo.',
  'Renewals come from uploaded contracts. Once a contract or remittance is on file, Atlas ranks renewals by money below the published median.':
    'Las renovaciones salen de los contratos subidos. Cuando haya un contrato o una remesa en archivo, Atlas ordena las renovaciones por el dinero bajo la mediana publicada.',
  'Nothing missing: bills when the month closes.': 'No falta nada: se factura al cerrar el mes.',
  'nothing missing: bills when the month closes': 'no falta nada: se factura al cerrar el mes',
  'Open appeal': 'Abrir apelación',
  Panel: 'Panel',
  'Pick a row to see it here.': 'Elija una fila para verla aquí.',
  'Pick a rate to see the brief.': 'Elija una tarifa para ver el informe.',
  'Practitioner to sign': 'Firma pendiente del profesional',
  'Published median': 'Mediana publicada',
  'Review claims': 'Revisar reclamaciones',
  'The PDF bundle is not built yet. Each claim’s evidence pack opens from the claim.':
    'El paquete PDF aún no está disponible. La evidencia de cada reclamación se abre desde la reclamación.',
  'The published range': 'El rango publicado',
  'Underpayments need your contract rates: upload a contract or remittance in Contracts.':
    'Los pagos bajos necesitan sus tarifas de contrato: suba un contrato o una remesa en Contratos.',
  'What it assumes': 'Qué supone',
  'Wings are not on the claims yet: each claim needs the resident’s wing before money can be split by wing.':
    'Las alas aún no figuran en las reclamaciones: cada reclamación necesita el ala del residente para repartir el dinero por ala.',
  'appeal outcomes are not tracked yet': 'los resultados de las apelaciones aún no se registran',
  'diagnosis missing': 'falta el diagnóstico',
  'documented and billable this month': 'documentado y facturable este mes',
  'gap –': 'brecha –',
  'needs consent': 'falta consentimiento',
  'needs a care plan': 'falta un plan de cuidados',
  'needs remittance dates': 'faltan fechas de remesa',
  'needs your contract rates on file': 'faltan sus tarifas de contrato en archivo',
  'no bundle exported yet': 'aún no se exportó ningún paquete',
  'no reference': 'sin referencia',
  'not in public files': 'no está en los archivos públicos',
  'open a claim for its evidence pack': 'abra una reclamación para ver su evidencia',
  'proposed 2027 rates not loaded yet': 'tarifas propuestas de 2027 aún sin cargar',
  published: 'publicada',
  'rate not loaded': 'tarifa sin cargar',
  'renewal date unknown': 'fecha de renovación desconocida',
  'sent, waiting on the payer': 'enviadas, esperando al pagador',
  'with the scenario you picked': 'con el escenario que eligió',
  'Consent rate': 'Tasa de consentimiento',
  Uptake: 'Adopción',
  'Denial rate': 'Tasa de denegación',
  'Ready to send. Nothing to do.': 'Lista para enviar. Nada que hacer.',
  'Nothing is blocking money right now.': 'Nada está frenando el dinero ahora.',
  'Every resident in scope has what a claim needs.': 'Cada residente tiene lo que necesita una reclamación.',
  'Nothing to appeal this week.': 'Nada que apelar esta semana.',
  'No denial has an open appeal window.': 'Ninguna denegación tiene un plazo de apelación abierto.',
  'Choose one building in the facility switcher to see this.': 'Elija un edificio en el selector de centros para ver esto.',
  'Choose one building to compare rates.': 'Elija un edificio para comparar tarifas.',
  'choose one building': 'elija un edificio',
  'Nothing is waiting on you today.': 'Hoy no hay nada pendiente.',
  'All done for today': 'Todo hecho por hoy',
  Sent: 'Enviado',
  'Nothing blocked': 'Nada bloqueado',
  'New blockers appear here as claims are drafted': 'Los nuevos bloqueos aparecen aquí al redactar reclamaciones',
  'Residents show for one building at a time': 'Los residentes se muestran de un edificio a la vez',
  'No paid or denied claims in this period.': 'No hay reclamaciones pagadas ni denegadas en este periodo.',
  'No claims yet': 'Aún no hay reclamaciones',
  'No claims yet. Figures appear after the first claims are paid.': 'Aún no hay reclamaciones. Las cifras aparecen cuando se paguen las primeras.',
  'Nothing stands out for this row.': 'Nada destaca en esta fila.',
  'Loading published rates…': 'Cargando tarifas publicadas…',
  'No published commercial rate for this code in your region.': 'No hay tarifa comercial publicada para este código en su región.',
  'Not yet checked': 'Aún sin revisar',
  'not yet checked': 'aún sin revisar',
  'none in the files': 'ninguna en los archivos',
  'not on the schedule': 'no está en el arancel',
  'Nothing matches this filter.': 'Nada coincide con este filtro.',
  'No unbilled codes this month. Every qualifying resident is billed or held.':
    'No hay códigos sin facturar este mes. Cada residente que califica está facturado o retenido.',
  'Nothing blocked. Every resident in scope has what a claim needs.': 'Nada bloqueado. Cada residente tiene lo que necesita una reclamación.',
  'No denials on file for this building.': 'No hay denegaciones en archivo para este edificio.',
  'No billing patterns to compare yet: the building has no billed months in the window.':
    'Aún no hay patrones de facturación que comparar: el edificio no tiene meses facturados en el periodo.',
  'No forecast for this scope.': 'No hay pronóstico para este alcance.',
  'Nothing matches. Clear the search or pick another payer.': 'Nada coincide. Borre la búsqueda o elija otro pagador.',
  'Ready now': 'Listo ya',
  'Too few claims': 'Muy pocas reclamaciones',
  'Too few claims to compare.': 'Muy pocas reclamaciones para comparar.',
  'Needs a visit': 'Falta una visita',
  'Ready to send': 'Listas para enviar',
  'Minutes short': 'Faltan minutos',
  'Fix and resubmit': 'Corregir y reenviar',
  'Rebill other payer': 'Refacturar a otro pagador',
  Close: 'Cerrar',
  'Started ✓': 'Iniciado ✓',
  'Done ✓': 'Hecho ✓',
  'Sent ✓': 'Enviado ✓',
  'Fixed ✓': 'Corregido ✓',
  Add: 'Agregar',
  'Add to forecast': 'Agregar al pronóstico',
  'Ask Atlas': 'Preguntar a Atlas',
  Record: 'Registro',
  View: 'Ver',
  Export: 'Exportar',
  Portfolio: 'Cartera',
  Network: 'Red',
  'This building': 'Este edificio',
  'Not started': 'Sin iniciar',
  'no network on file': 'sin red en archivo',
  'no checklist sent': 'ninguna lista enviada',
  'No network yet. Buildings join once antitrust counsel has designed the structure; owners never see each other’s rates.':
    'Aún no hay red. Los edificios se suman cuando el abogado antimonopolio haya diseñado la estructura; los propietarios nunca ven las tarifas de otros.',
  "No network yet. Each building's readiness shows here once the network is set up.":
    'Aún no hay red. La preparación de cada edificio aparece aquí cuando la red esté configurada.',
  '0 buildings · no member rates shown here': '0 edificios · aquí no se muestran tarifas de miembros',
  'Medicare FFS rates': 'Tarifas de Medicare FFS',
  national: 'nacional',
  'rate files': 'archivos de tarifas',
  'Dementia care plan': 'Plan de cuidados para demencia',
  'Ready now · staff already log 60+ min': 'Listo ya · el personal ya registra más de 60 min',
  'Recorded for follow-up': 'Registrado para seguimiento',
  'That did not go through.': 'No se completó.',
  'Atlas answer': 'Respuesta de Atlas',
  'Rate from your remittances': 'Tarifa de sus remesas',
  'No NPI on the claim': 'Sin NPI en la reclamación',
  'No practitioner on the claim': 'Sin profesional en la reclamación',
  'Payer not on file': 'Pagador no registrado',
  'Other payers': 'Otros pagadores',
  'Code hidden': 'Código oculto',
  Resident: 'Residente',
  'Close ✕': 'Cerrar ✕',
  // The V6 features brought back into the V7 tabs.
  'just now': 'ahora mismo',
  'Practitioner enrollment': 'Inscripción de profesionales',
  PRACTITIONER: 'PROFESIONAL',
  'CLEARINGHOUSE BATCH': 'LOTE DE LA CÁMARA DE COMPENSACIÓN',
  Acknowledgements: 'Acuses',
  '999 · file accepted': '999 · archivo aceptado',
  '277CA · claim status': '277CA · estado de la reclamación',
  Revalidation: 'Revalidación',
  'Medicare (PECOS)': 'Medicare (PECOS)',
  'Billing-agent authorization': 'Autorización de agente de facturación',
  'No date on file': 'Sin fecha en archivo',
  'Resent ✓': 'Reenviado ✓',
  'Enrolled with every payer they cover. Nothing to do.': 'Inscrito con cada pagador que cubre. Nada que hacer.',
  'Enrollment comes from PECOS and the state Medicaid file. Aithora chases it; the practitioner signs.':
    'La inscripción sale de PECOS y del archivo estatal de Medicaid. Aithora le da seguimiento; el profesional firma.',
  'The enrolled practitioner submits every claim · Aithora never does': 'El profesional inscrito presenta cada reclamación · Aithora nunca lo hace',
  'The enrolled practitioner submits every claim. Aithora never does.': 'El profesional inscrito presenta cada reclamación. Aithora nunca lo hace.',
  'No practitioners on file for this building yet.': 'Aún no hay profesionales en archivo para este edificio.',
  'No batches have left yet. Claims go to the clearinghouse once the practitioner signs.':
    'Aún no ha salido ningún lote. Las reclamaciones van a la cámara de compensación cuando el profesional firma.',
  'Codes are hidden for this building. Every total and rate is yours to see; code-level lines come with the Capture plan.':
    'Los códigos están ocultos para este edificio. Puede ver cada total y cada tarifa; las líneas por código vienen con el plan de Captura.',
  'Requested · Aithora confirms within one business day': 'Solicitado · Aithora confirma en un día hábil',
  'Codes are hidden for your role': 'Los códigos están ocultos para su rol',
  'Rates by payer and code show here once code-level access is on.': 'Las tarifas por pagador y código aparecen aquí cuando se activa el acceso por código.',
  'The negotiation brief needs the codes. It opens here once code-level access is on.': 'El informe de negociación necesita los códigos. Se abre aquí cuando se activa el acceso por código.',
  'Routes them to the enrolled practitioner to sign. Aithora never submits a claim.': 'Las envía al profesional inscrito para que las firme. Aithora nunca presenta una reclamación.',
  Medicare: 'Medicare',
  Medicaid: 'Medicaid',
  'Search code, payer, name': 'Buscar código, pagador, nombre',
};

const MONTHS: Record<string, string> = { Jan: 'ene.', Feb: 'feb.', Mar: 'mar.', Apr: 'abr.', May: 'may.', Jun: 'jun.', Jul: 'jul.', Aug: 'ago.', Sep: 'sep.', Oct: 'oct.', Nov: 'nov.', Dec: 'dic.' };
const pl = (n: string, one: string, many: string) => (n === '1' ? one : many);

export function installCapture(A: EsEngine): void {
  A.add(FROM_DESIGN);
  A.add(OURS);
  const T = (s: string) => A.tr(s);
  const low = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  // Dates as the design writes them: "Aug 2026", "Nov 1 2025".
  const mon = (t: string) => t.replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2} )?(\d{4})\b/g, (_x, mm: string, d: string | undefined, y: string) => (d ? d.trim() + ' ' : '') + MONTHS[mm] + ' ' + y);
  // From the V7 design (rules its Capture strings use), adapted to this engine.
  // Hidden codes numbered when two share a category ("Code hidden 1", "Code hidden 2").
  A.rule(/^Code hidden (\d+)$/, (m) => 'Código oculto ' + m[1]);
  A.rule(/^(\d+) residents qualify · (.+)$/, (m) => m[1] + ' residentes califican · ' + (T(m[2]) || m[2]));
  A.rule(/^(.+) · (\$[\d.,]+) this month$/, (m) => (T(m[1]) || m[1]) + ' · ' + m[2] + ' este mes');
  A.rule(/^(\$[\d.,]+) this month$/, (m) => m[1] + ' este mes');
  A.rule(/^(.+) · (\d+) d to (appeal|dispute|fix)$/, (m) => (T(m[1]) || m[1]) + ' · ' + m[2] + ' d para ' + (m[3] === 'appeal' ? 'apelar' : m[3] === 'dispute' ? 'disputar' : 'corregir'));
  A.rule(/^(\d+) d to (appeal|dispute|fix)$/, (m) => m[1] + ' d para ' + (m[2] === 'appeal' ? 'apelar' : m[2] === 'dispute' ? 'disputar' : 'corregir'));
  A.rule(/^(.+) (↓|↑)$/, (m) => { const t = T(m[1]); return t ? t + ' ' + m[2] : null; });
  A.rule(/^(→|✓) (.+)$/, (m) => { const t = T(m[2]); return t ? m[1] + ' ' + t : null; });
  A.rule(/^vs last (month|quarter|12 months)$/, (m) => 'frente ' + ({ month: 'al mes pasado', quarter: 'al trimestre pasado', '12 months': 'a los 12 meses anteriores' } as Record<string, string>)[m[1]]);
  A.rule(/^last (month|quarter|12 months), your claims$/, (m) => ({ month: 'último mes', quarter: 'último trimestre', '12 months': 'últimos 12 meses' } as Record<string, string>)[m[1]] + ', sus reclamaciones');
  // Ours: counts, money and names built into a line.
  A.rule(/^(\d+) (left)$/, (m) => m[1] + ' pendientes');
  A.rule(/^(\d+) ready now$/, (m) => m[1] + ' ' + pl(m[1], 'listo ya', 'listos ya'));
  A.rule(/^(\d+) ready$/, (m) => m[1] + ' ' + pl(m[1], 'listo', 'listos'));
  A.rule(/^(\d+) blockers?$/, (m) => m[1] + ' ' + pl(m[1], 'bloqueo', 'bloqueos'));
  A.rule(/^(\d+) codes still to load$/, (m) => m[1] + ' códigos por cargar');
  A.rule(/^every code has a rate$/, () => 'cada código tiene tarifa');
  A.rule(/^(\d+) of (\d+)$/, (m) => m[1] + ' de ' + m[2]);
  A.rule(/^(\d+) of (\d+) (min|days)$/, (m) => m[1] + ' de ' + m[2] + (m[3] === 'min' ? ' min' : ' días'));
  A.rule(/^(\d+) of (\d+) min · month closes in (\d+) d$/, (m) => m[1] + ' de ' + m[2] + ' min · el mes cierra en ' + m[3] + ' d');
  A.rule(/^(\d+) min short · (\d+) d$/, (m) => 'faltan ' + m[1] + ' min · ' + m[2] + ' d');
  A.rule(/^(\d+) d to month end$/, (m) => m[1] + ' d para fin de mes');
  A.rule(/^minutes must be logged by (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d+)$/, (m) => 'los minutos deben registrarse antes del ' + m[2] + ' de ' + MONTHS[m[1]]);
  A.rule(/^Needs (\w+) consent$/, (m) => 'Falta consentimiento de ' + m[1]);
  A.rule(/^Needs consent from (\d+) residents?$/, (m) => 'Falta el consentimiento de ' + m[1] + ' ' + pl(m[1], 'residente', 'residentes'));
  A.rule(/^(\d+) short on minutes or device days$/, (m) => 'a ' + m[1] + ' les faltan minutos o días de dispositivo');
  A.rule(/^Needs a care plan \((\d+)\)$/, (m) => 'Falta un plan de cuidados (' + m[1] + ')');
  A.rule(/^Get (\d+) consents?$/, (m) => 'Pedir ' + m[1] + ' ' + pl(m[1], 'consentimiento', 'consentimientos'));
  A.rule(/^Log (\d+) minutes? for (.+)$/, (m) => 'Registrar ' + m[1] + (m[1] === '1' ? ' minuto para ' : ' minutos para ') + m[2]);
  A.rule(/^Appeal (.+) in (\d+) days$/, (m) => 'Apelar ante ' + m[1] + ' en ' + m[2] + ' días');
  A.rule(/^(\d+) claims shown of (\d+)$/, (m) => m[1] + ' reclamaciones de ' + m[2]);
  A.rule(/^(\d+) items · reason codes are standard CARC codes$/, (m) => m[1] + ' elementos · los códigos de motivo son códigos CARC estándar');
  A.rule(/^(\d+) codes · the enrolled practitioner bills · documentation support by Aithora$/, (m) => m[1] + ' códigos · factura el profesional inscrito · apoyo de documentación de Aithora');
  A.rule(/^(\d+) flags?$/, (m) => m[1] + ' ' + pl(m[1], 'alerta', 'alertas'));
  A.rule(/^(\d+) high flags · (\d+) medium$/, (m) => m[1] + ' alertas altas · ' + m[2] + ' medias');
  A.rule(/^(\d+) rows · delivered (\S+) · billed (\S+) · paid (\S+)( · SAMPLE)?$/, (m) => m[1] + ' filas · prestado ' + m[2] + ' · facturado ' + m[3] + ' · pagado ' + m[4] + (m[5] ? ' · MUESTRA' : ''));
  A.rule(/^(\d+) published rates · (\d+) payers$/, (m) => m[1] + ' tarifas publicadas · ' + m[2] + ' pagadores');
  A.rule(/^(\d+) payers · rate files$/, (m) => m[1] + ' pagadores · archivos de tarifas');
  A.rule(/^median of (\d+)$/, (m) => 'mediana de ' + m[1]);
  A.rule(/^median (\$[\d.,]+)$/, (m) => 'mediana ' + m[1]);
  A.rule(/^(25th|75th) (\$[\d.,]+)$/, (m) => 'pct. ' + m[1].slice(0, 2) + ' ' + m[2]);
  A.rule(/^ref (\$[\d.,]+)$/, (m) => 'ref. ' + m[1]);
  A.rule(/^(\$[\d.,K]+) appealable$/, (m) => m[1] + ' apelables');
  A.rule(/^(\$[\d.,K]+) of care eligible was not billed\.$/, (m) => m[1] + ' de cuidado elegible no se facturó.');
  A.rule(/^(\$[\d.,K]+) \/ yr$/, (m) => m[1] + ' / año');
  A.rule(/^(\$[\d.,K]+) \/ mo$/, (m) => m[1] + ' / mes');
  A.rule(/^([+−-]\$[\d.,K]+) over the current path$/, (m) => m[1] + ' sobre el rumbo actual');
  A.rule(/^Adds about (\$[\d.,K]+) a month against the current path$/, (m) => 'Suma unos ' + m[1] + ' al mes frente al rumbo actual');
  A.rule(/^at (\d+)% uptake$/, (m) => 'con ' + m[1] + '% de adopción');
  A.rule(/^per (\d+) agreements?$/, (m) => 'según ' + m[1] + ' ' + pl(m[1], 'acuerdo', 'acuerdos'));
  A.rule(/^(\d+) eligible, not started$/, (m) => m[1] + ' elegibles, sin iniciar');
  A.rule(/^Denials ([\d.]+)%$/, (m) => 'Denegaciones ' + m[1] + '%');
  A.rule(/^gap (\d+)%$/, (m) => 'brecha ' + m[1] + '%');
  A.rule(/^(Consent rate|Uptake|Denial rate|Census|MA share): ([\d.]+%?)$/, (m) => (T(m[1]) || m[1]) + ': ' + m[2]);
  A.rule(/^Send all consent forms \((\d+)\)$/, (m) => 'Enviar todos los consentimientos (' + m[1] + ')');
  A.rule(/^(\d+) consent links? sent$/, (m) => m[1] + ' ' + pl(m[1], 'enlace de consentimiento enviado', 'enlaces de consentimiento enviados'));
  A.rule(/^Enrollment queued for (\S+)$/, (m) => 'Inscripción en cola para ' + m[1]);
  A.rule(/^Too late · (\d+) days$/, (m) => 'Demasiado tarde · ' + m[1] + ' días');
  A.rule(/^(\d+) residents qualify · No published rate$/, (m) => m[1] + ' residentes califican · Sin tarifa publicada');
  A.rule(/^(\d+) claims? can[’']t go out yet\.$/, (m) => m[1] + ' ' + pl(m[1], 'reclamación aún no puede salir.', 'reclamaciones aún no pueden salir.'));
  A.rule(/^(.+), (\d+) days left\.$/, (m) => m[1] + ', quedan ' + m[2] + ' días.');
  A.rule(/^Stuck at ([^:]+): (.+)\.$/, (m) => {
    const step = T(cap(m[1]));
    const blk = T(cap(m[2]));
    return step && blk ? 'Detenida en ' + low(step) + ': ' + low(blk) + '.' : null;
  });
  // "2 need consent, 1 has no care plan and 3 are short on minutes, with 16 days left in the month."
  const PART: [RegExp, (n: string) => string][] = [
    [/^(\d+) needs? consent$/, (n) => n + ' ' + pl(n, 'necesita consentimiento', 'necesitan consentimiento')],
    [/^(\d+) (?:has|have) no care plan$/, (n) => n + ' ' + pl(n, 'no tiene plan de cuidados', 'no tienen plan de cuidados')],
    [/^(\d+) (?:is|are) missing a diagnosis$/, (n) => n + ' ' + pl(n, 'no tiene diagnóstico', 'no tienen diagnóstico')],
    [/^(\d+) (?:is|are) short on minutes$/, (n) => n + ' ' + pl(n, 'tiene minutos de menos', 'tienen minutos de menos')],
    [/^(\d+) waits? on the practitioner$/, (n) => n + ' ' + pl(n, 'espera al profesional', 'esperan al profesional')],
  ];
  A.rule(/^(.+?)(?:, with (\d+) days left in the month)?\.$/, (m) => {
    const parts = m[1].split(/, | and /);
    const out: string[] = [];
    for (const p of parts) {
      const hit = PART.find(([re]) => re.test(p));
      if (!hit) return null;
      out.push(hit[1](p.match(hit[0])![1]));
    }
    const joined = out.length > 1 ? out.slice(0, -1).join(', ') + ' y ' + out[out.length - 1] : out[0];
    return cap(joined) + (m[2] ? ', con ' + m[2] + ' días restantes en el mes.' : '.');
  });
  A.rule(/^What insurers publish for (\S+) in (.+)\. Upload your contract or a remittance to see where your own rate sits and what to ask for at renewal\.$/, (m) =>
    'Lo que publican las aseguradoras para ' + m[1] + ' en ' + m[2] + '. Suba su contrato o una remesa para ver dónde queda su propia tarifa y qué pedir en la renovación.');
  A.rule(/^Medicare Advantage rates are not in the public files\. Upload a remittance or your contract and we compare it with the Medicare rate( of (\$[\d.,]+))?\.$/, (m) =>
    'Las tarifas de Medicare Advantage no están en los archivos públicos. Suba una remesa o su contrato y lo comparamos con la tarifa de Medicare' + (m[2] ? ' de ' + m[2] : '') + '.');
  A.rule(/^Published median (\$[\d.,]+)(?: · middle half (\$[\d.,]+)–(\$[\d.,]+))? · (\d+) rates\.$/, (m) =>
    'Mediana publicada ' + m[1] + (m[2] ? ' · mitad central ' + m[2] + '–' + m[3] : '') + ' · ' + m[4] + ' tarifas.');
  A.rule(/^Your rates vs\. what each payer publishes in (.+) · public files and your contracts only$/, (m) =>
    'Sus tarifas frente a lo que publica cada pagador en ' + (m[1] === 'your region' ? 'su región' : m[1]) + ' · solo archivos públicos y sus contratos');
  A.rule(/^Your rate for each code and payer against what that payer publishes in (.+)\. Public files and your own contracts only\.$/, (m) =>
    'Su tarifa por código y pagador frente a lo que ese pagador publica en ' + (m[1] === 'your region' ? 'su región' : m[1]) + '. Solo archivos públicos y sus propios contratos.');
  A.rule(/^Ranges from insurer rate files published under federal price-transparency rules(, \w{3} \d{4})?\. Medicare Advantage rates are not in those files\. Every rate shows its source and date in the panel\. Counsel review required before release\.$/, (m) =>
    'Rangos de los archivos de tarifas de aseguradoras publicados bajo las reglas federales de transparencia de precios' + (m[1] ? ', ' + mon(m[1].slice(2)) : '') + '. Las tarifas de Medicare Advantage no están en esos archivos. Cada tarifa muestra su fuente y fecha en el panel. Requiere revisión legal antes de publicarse.');
  A.rule(/^Denied as (.+?)( \(\S+\))?\. (.+) Atlas drafts the appeal with the evidence on file\. A person reviews and sends it\.$/, (m) =>
    'Denegada: ' + m[1] + (m[2] ?? '') + '. ' + m[3] + ' Atlas redacta la apelación con la evidencia en archivo. Una persona la revisa y la envía.');
  A.rule(/^PFS (\d{4}) · (.+)$/, (m) => 'PFS ' + m[1] + ' · ' + (T(m[2]) || m[2]));
  A.rule(/^PUBLISHED rates · PFS (\d{4})$/, (m) => 'Tarifas PUBLISHED · PFS ' + m[1]);
  A.rule(/^NPI on file · agreement (\w{3} \d{4})(?: · (\d+)%)?$/, (m) => 'NPI en archivo · acuerdo ' + mon(m[1]) + (m[2] ? ' · ' + m[2] + '%' : ''));
  A.rule(/^NPI on file · No agreement on file$/, () => 'NPI en archivo · Sin acuerdo en archivo');
  A.rule(/^(.+) · Fixed published rate$/, (m) => m[1] + ' · Tarifa publicada fija');
  A.rule(/^(.+) · (\d+) residents · (\d+) months$/, (m) => m[1] + ' · ' + m[2] + ' residentes · ' + m[3] + ' meses');
  A.rule(/^(\d+) denials? · (\$[\d.,K]+)$/, (m) => m[1] + ' ' + pl(m[1], 'denegación', 'denegaciones') + ' · ' + m[2]);
  A.rule(/^(.+)\. There is no public benchmark for this pattern, so nothing is compared\.$/, (m) => { const t = T(m[1]); return t ? t + '. No hay referencia pública para este patrón, así que no se compara nada.' : null; });
  A.rule(/^([^.]+)\.$/, (m) => { const t = A.D[m[1]]; return t ? t + '.' : null; });
  A.rule(/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) (\d{1,2} )?(\d{4})$/, (m) => mon(m[0]));
  A.rule(/^Due (.+)$/, (m) => 'Vence ' + (/\d{4}$/.test(m[1]) ? mon(m[1]) : m[1] === 'within 90 days' ? 'en 90 días' : m[1]));
  A.rule(/^Physician Fee Schedule (\d{4}), (.+), released (.+)$/, (m) => 'Arancel de Médicos ' + m[1] + ', ' + m[2] + ', publicado el ' + mon(m[3]));
  A.rule(/^(.+) price-transparency rate files(?:, (\w{3} \d{4}))?$/, (m) => 'Archivos de tarifas de transparencia de precios de ' + m[1] + (m[2] ? ', ' + mon(m[2]) : ''));
  A.rule(/^(\d+) practitioners?$/, (m) => m[1] + ' ' + pl(m[1], 'profesional', 'profesionales'));
  A.rule(/^(\d+) batches? · 999 and 277CA acknowledgements$/, (m) => m[1] + ' ' + pl(m[1], 'lote', 'lotes') + ' · acuses 999 y 277CA');
  A.rule(/^(\d+) of (\d+) acknowledged$/, (m) => m[1] + ' de ' + m[2] + ' confirmadas');
  A.rule(/^(\d+) of (\d+) claims bounced before the payer saw them: (.+)\.$/, (m) => m[1] + ' de ' + m[2] + ' reclamaciones rebotaron antes de que el pagador las viera: ' + m[3] + '.');
  A.rule(/^Send (\d+) ready claims? to the practitioner$/, (m) => 'Enviar ' + m[1] + ' ' + pl(m[1], 'reclamación lista', 'reclamaciones listas') + ' al profesional');
  A.rule(/^(\d+) sent to the practitioner for signature$/, (m) => m[1] + ' ' + pl(m[1], 'enviada', 'enviadas') + ' al profesional para su firma');
  A.rule(/^(\d+) (min|h|d) ago$/, (m) => 'hace ' + m[1] + ' ' + m[2]);
  A.rule(/^(▲|▼) ([\d.]+) pt$/, (m) => m[1] + ' ' + m[2] + ' pt');
  A.rule(/^needs (\d+) more staff minutes?$/, (m) => 'faltan ' + m[1] + ' ' + pl(m[1], 'minuto', 'minutos') + ' del personal');
  A.rule(/^needs (\d+) more days? of device readings$/, (m) => 'faltan ' + m[1] + ' ' + pl(m[1], 'día', 'días') + ' de lecturas del dispositivo');
  // A footnote with the page's SAMPLE tag after it.
  A.rule(/^(.+) · SAMPLE$/, (m) => { const t = T(m[1]); return t ? t + ' · MUESTRA' : null; });
  A.rule(/^(.+) \((\d+)\)$/, (m) => { const t = T(m[1]); return t ? t + ' (' + m[2] + ')' : null; });
  // A line of parts joined by " · ": each part in Spanish, a name or a code as it is (V7 rule).
  A.rule(/^(.+? · .+)$/, (m) => {
    const parts = m[1].split(' · ');
    let hit = false;
    const out = parts.map((p) => {
      const t = T(p);
      if (t && t !== p) {
        hit = true;
        return t;
      }
      return A.isName(p) || !/[a-z]{3}/i.test(p) || /^[A-Z0-9-]+$/.test(p) ? p : null;
    });
    return hit && out.every((x) => x !== null) ? out.join(' · ') : null;
  });
}
